import { createAsyncThunk, createSlice, isPending, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { ISensorDataValuesDTO } from '../../model/dto/sensor-data-values-dto.model';
import { ISensorDataValuesByTuckImplementIdsDTO } from 'app/shared/model/dto/sensor-data-values-by-tuck-implement-ids-dto.model';

const initialState = {
  loading: false,
  errorMessage: null,
  odometerSensorValues: [] as Array<ISensorDataValuesDTO>,
  sensorDataValuesByTuckImplementIdsDTO: [] as Array<ISensorDataValuesByTuckImplementIdsDTO>,
  sensorDataValuesByTuckImplementIdDTO: [] as Array<ISensorDataValuesByTuckImplementIdsDTO>,
  updateSuccess: false,
  totalItems: 0,
};

export type SensorValueState = Readonly<typeof initialState>;

const apiUrl = 'api/sensor-data-values';

// Create a cancel token for the request
let cancelTokenSource = axios.CancelToken.source();

// Actions

interface IGetSensorDataBySensorIdsAndTruckImplementIds {
  truckImplementIds: Array<number>;
  loadingSensorDataValues: boolean;
  onSensorDataValues?: boolean;
}

export const getSensorDataBySensorIdsAndTruckImplementIds = createAsyncThunk(
  'sensorValue/fetch_sensor_data_values_by_truck_implement_ids',
  async ({ truckImplementIds, loadingSensorDataValues, onSensorDataValues }: IGetSensorDataBySensorIdsAndTruckImplementIds) => {
    if (cancelTokenSource && loadingSensorDataValues) {
      cancelTokenSource.cancel('Request canceled, because a new request was made.');
    }

    cancelTokenSource = axios.CancelToken.source();

    try {
      return await axios.get<ISensorDataValuesByTuckImplementIdsDTO[]>(`${apiUrl}/${truckImplementIds}`, {
        cancelToken: cancelTokenSource.token,
        params: { onSensorDataValues },
      });
    } catch (error) {
      if (axios.isCancel(error)) {
        console.error('Canceled request: ', error.message);
      } else {
        throw error;
      }
    }
  }
);

export const getSensorDataBySensorIdsAndTruckImplementId = createAsyncThunk(
  'sensorValue/fetch_sensor_data_values_by_truck_implement_id',
  async ({ truckImplementIds, loadingSensorDataValues, onSensorDataValues }: IGetSensorDataBySensorIdsAndTruckImplementIds) => {
    if (cancelTokenSource && loadingSensorDataValues) {
      cancelTokenSource.cancel('Request canceled, because a new request was made.');
    }

    cancelTokenSource = axios.CancelToken.source();

    try {
      return await axios.get<ISensorDataValuesByTuckImplementIdsDTO[]>(`${apiUrl}/${truckImplementIds}`, {
        cancelToken: cancelTokenSource.token,
        params: { onSensorDataValues },
      });
    } catch (error) {
      if (axios.isCancel(error)) {
        console.error('Canceled request: ', error.message);
      } else {
        throw error;
      }
    }
  }
);

export const getLatestOdometerSensorDataValues = createAsyncThunk('sensorValue/fetch_sensor_data_values', async (truckImplementIds: Array<number>) => {
  const requestUrl = `${apiUrl}/latest-odometer/${truckImplementIds}`;
  return axios.get<ISensorDataValuesDTO[]>(requestUrl);
});

// slice

export const SensorValueSlice = createSlice({
  name: 'sensorValue',
  initialState: initialState as SensorValueState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSensorDataBySensorIdsAndTruckImplementIds.fulfilled, (state, action) => {
        state.loading = false;
        state.sensorDataValuesByTuckImplementIdsDTO = action.payload.data;
      })
      .addCase(getSensorDataBySensorIdsAndTruckImplementId.fulfilled, (state, action) => {
        state.loading = false;
        state.sensorDataValuesByTuckImplementIdDTO = action.payload.data;
      })
      .addCase(getLatestOdometerSensorDataValues.fulfilled, (state, action) => {
        state.loading = false;
        state.odometerSensorValues = action.payload.data;
      })
      .addMatcher(isPending(getLatestOdometerSensorDataValues, getSensorDataBySensorIdsAndTruckImplementIds, getSensorDataBySensorIdsAndTruckImplementId), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      });
  },
});

export const { reset } = SensorValueSlice.actions;

// Reducer
export default SensorValueSlice.reducer;
